import { BaseLayout, type NavSection } from './BaseLayout';
import { SetupLayout } from './SetupLayout';
import {
  LayoutDashboard, GraduationCap, Users, BookOpen, Clock, UserCog,
  ClipboardList, Megaphone, Building2, MapPin, Globe, Network, Shield, DoorOpen, FolderOpen, ContactRound, FileText,
  UserMinus, Sparkles, LayoutTemplate, Tv, BarChart3, User, Bell, MessageSquare, Calendar,
} from 'lucide-react';
import { useSetupBadges } from '../../hooks/useSetupBadges';
import { useSetupStatus } from '../../hooks/useSetupStatus';
import { useMemo } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useProfileBrandColor } from '../../theme/useProfileBrandColor';
import { useLocation } from 'react-router-dom';

// ═══════════════════════════════════════════════
// Sidebar RESTRITO (durante setup inicial)
// So mostra itens de configuracao, na ordem correta
// ═══════════════════════════════════════════════

const NAV_SETUP: NavSection[] = [
  {
    title: null,
    items: [
      { to: '/admin/setup', label: 'GUIA DE IMPLANTAÇÃO', icon: Sparkles, badgeKey: 'dashboard' },
    ],
  },
  {
    title: 'Configura\u00e7\u00e3o',
    items: [
      { to: '/admin/setup?step=equipe', label: 'Equipe', icon: UserCog, badgeKey: 'equipe' },
      { to: '/admin/setup?step=turnos', label: 'Turnos', icon: Clock, badgeKey: 'turnos' },
      { to: '/admin/setup?step=materias', label: 'Mat\u00e9rias', icon: BookOpen, badgeKey: 'materias' },
      { to: '/admin/setup?step=professores', label: 'Professores', icon: Users, badgeKey: 'professores' },
      { to: '/admin/setup?step=turmas', label: 'Turmas', icon: GraduationCap, badgeKey: 'turmas' },
      { to: '/admin/setup?step=responsaveis', label: 'Respons\u00e1veis', icon: ContactRound, badgeKey: 'responsaveis' },
      { to: '/admin/setup?step=alunos', label: 'Alunos', icon: Users, badgeKey: 'alunos' },
      { to: '/admin/setup?step=grade', label: 'Grade', icon: Calendar, badgeKey: 'grade' },
    ],
  },
];

export function AdminLayout() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  // Verificar TODAS as etapas da escola (sem filtro de cargo) para decidir
  // se o sidebar mostra menu de setup ou menu operacional completo.
  const setupStatus = useSetupStatus();
  const badges = useSetupBadges();
  const { prontoParaOperar } = setupStatus;
  const { brandColor, brandGradient } = useProfileBrandColor('DIRETOR');

  const sections = useMemo(() => {
    if (!prontoParaOperar) return NAV_SETUP;

    const isDiretor =
      user?.perfil === 'ADMIN' ||
      (user?.perfil === 'ESCOLA' && ((user?.cargo || '').toUpperCase().includes('DIR') || !user?.cargo));

    const governanca = [
      { to: '/admin/chamadas-gestao', label: 'Governança Chamadas', icon: Shield, badgeKey: 'governanca' },
      { to: '/admin/acesso', label: 'Acesso / Portaria', icon: DoorOpen, badgeKey: 'acesso' },
    ];
    if (isDiretor) {
      governanca.push({
        to: '/admin/aprovacoes',
        label: 'Aprovações de Acesso',
        icon: Shield,
        badgeKey: 'aprovacoes',
      });
    }

    const topItems: NavSection['items'] = [
      { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, badgeKey: 'dashboard' },
    ];
    if (!prontoParaOperar) {
      topItems.push({ to: '/admin/setup', label: 'Trilha de Implantação', icon: Sparkles, badgeKey: 'setup' as any });
    }
    topItems.push({ to: '/admin/ead', label: 'Video Aulas', icon: GraduationCap });

    return [
      {
        title: null,
        items: topItems,
      },
      {
        title: 'Escola',
        alwaysExpanded: true,
        items: [
          {
            to: '#',
            label: 'Pessoas e Contas',
            icon: Users,
            subItems: [
              { to: '/admin/responsaveis', label: 'Responsáveis', badgeKey: 'responsaveis' },
              { to: '/admin/alunos', label: 'Alunos', badgeKey: 'alunos' },
              { to: '/admin/professores', label: 'Professores', badgeKey: 'professores' },
              { to: '/admin/equipe', label: 'Equipe', badgeKey: 'equipe' },
            ],
          },
          {
            to: '#',
            label: 'Cadastros Base',
            icon: BookOpen,
            subItems: [
              { to: '/admin/turnos', label: 'Turnos', badgeKey: 'turnos' },
              { to: '/admin/materias', label: 'Matérias', badgeKey: 'materias' },
              { to: '/admin/turmas', label: 'Turmas', badgeKey: 'turmas' },
              { to: '/admin/dispositivos', label: 'Dispositivos', badgeKey: 'dispositivos' },
            ],
          },
        ],
      },
      {
        title: 'Operacional',
        alwaysExpanded: true,
        items: [
          { to: '/admin/chamada', label: 'Chamada', icon: ClipboardList, badgeKey: 'chamada' },
          { to: '/admin/cobertura', label: 'Cobertura de Grade', icon: UserMinus },
          { to: '/admin/justificativas', label: 'Justificativas de Falta', icon: FileText, badgeKey: 'justificativas' },
        ],
      },
      {
        title: 'Comunicação',
        alwaysExpanded: true,
        items: [
          { to: '/admin/comunicados', label: 'Comunicados', icon: Megaphone, badgeKey: 'comunicados' },
          { to: '/admin/midias', label: 'Banco de Mídias', icon: FolderOpen, badgeKey: 'midias' },
          { to: '/admin/mural', label: 'Mural Digital', icon: LayoutTemplate },
          { to: '/admin/estatisticas', label: 'Estatísticas', icon: BarChart3 },
        ],
      },
      {
        title: 'Governança',
        alwaysExpanded: true,
        items: governanca,
      },
      {
        title: 'Gestão',
        adminOnly: true,
        items: [
          { to: '/admin/gestao/redes', label: 'Redes', icon: Network },
          { to: '/admin/gestao/estados', label: 'Estados', icon: Globe },
          { to: '/admin/gestao/municipios', label: 'Municípios', icon: MapPin },
          { to: '/admin/gestao/escolas', label: 'Escolas', icon: Building2 },
          { to: '/admin/gestao/usuarios', label: 'Usuários', icon: Shield },
        ],
      },
      {
        title: 'Minha Conta',
        items: [
          { to: '/admin/perfil', label: 'Meu Perfil', icon: User },
          { to: '/admin/alertas', label: 'Meus Comunicados', icon: Megaphone, badgeKey: 'alertas' },
        ],
      },
    ];
  }, [prontoParaOperar, user]);

  if (location.pathname.startsWith('/admin/setup')) {
    return <SetupLayout sections={NAV_SETUP} setupGuard={setupStatus} />;
  }

  return (
    <BaseLayout
      sections={sections}
      headerTitle="Painel Escola"
      badges={badges}
      brandColor={brandColor}
      brandGradient={brandGradient}
      navSize="md"
    />
  );
}
